// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  BasicUserInfo,
  DecodedIDTokenPayload,
} from '@asgardeo/auth-spa';

export enum ExamState {
  ATTEMPTED = "Attempted",
  ACTIVE = "Active",
  FAILED = "Failed",
  EXPIRED = "Expired",
  PASSED = "Passed",
  REGISTERED = "Registered",
  COMPLETED = "Completed",
  IN_PROGRESS = "In Progress",
}

export enum BannerState {
  FAILED = "failed",
  LOADING = "loading",
  IDLE = "idle",
  SUCCESS = "success",
}

export enum RequestState {
  FAILED = "failed",
  SUCCESS = "success"
}

export interface BannerToggleDetail {
  welcome?: boolean;
  notification?: boolean;
}

export interface AppStatus {
  isShowMaintenanceBanner: boolean | null;
  maintenanceBannerTitle: string | null;
  maintenanceBannerContent: string | null;
  isAppInMaintenanceState: boolean | null;
  isExamResumptionEnabled: boolean;
}

export interface AuthState {
  userInfo: BasicUserInfo | null;
  idToken: string | null;
  decodedIdToken: DecodedIDTokenPayload | null;
  isAuthenticated: boolean;
  errorMessage: string | null;
  status: "failed" | "loading" | "idle" | "success";
  loading: boolean;
}

export interface CurrentExamState {
  state: "idle" | "ongoing" | "completed";
  isAllAnswered: boolean;
}

export interface AuthData {
  userInfo: BasicUserInfo;
  idToken: string;
  decodedIdToken: DecodedIDTokenPayload;
}

export interface CertificationsPayload {
  id: string;
  contactId: string;
  name: string;
  displayName: string;
  isAwarded: boolean;
  awardedDate: string;
  badgeLink: string;
  contact: ContactInterface;
  certificationExpirationDate?: string;
}

export interface CertificateHolder {
  certificationId: string;
  fullName: string;
  marks: number;
  position?: number|null;
}

export interface LeaderboardQuarter {
  year: string;
  quarter: string;
  topCertificateHolders?: CertificateHolder[];
  topCertificateHolder?: CertificateHolder;
}

export interface LeaderboardResponse {
  leaderboardQuarter?: LeaderboardQuarter;
  previousQuarterLeaderboard?: LeaderboardQuarter;
}

export interface AwardsPayload {
  id: string;
  contactId: string;
  certificationId: string;
  name: string;
  date: string;
  quarter: string|null;
  template: AwardTemplateType;
  year: string;
  logo: string;
  position: string;
  region: string;
  contact: AwardContactInterface;
}

export interface RegisteredExamsPayload {
  id: string;
  state: string;
  examId: string;
  expirationDate: string;
  examType: string;
  examVisibleType: string;
  examPaperType: string;
  isExtended?: boolean;
}

export interface ExamPaperPayload {
  paperId: string | null;
  questionType: string;
  nameOfPaper: string;
  timeForPaper: number;
  description: string;
  generatedQuestionSet: QuestionModalPractical[];
}

export interface UserFeedbackInterface {
  date: string;
  certificationId: string;
  certificationType: string;
  registration: number | null;
  materials: number | null;
  clarity: number | null;
  recommendation: number | null;
  feedback: string | null;
}

export interface ExamPaperRequestInterface {
  tokenId: string;
  examId: string;
  user: UserInterface;
}

export interface ExamSubmissionRequestInterface {
  examId: string;
  answers: ExamSnapshotPayload;
}

export interface ExamSnapshotPayload {
  paperId: string;
  answers: AnswerListInterface[];
}

export interface AnswerListInterface {
  answerBlockId: number;
  answers: string[];
}

export interface UserInterface {
  id: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
}

export interface QuestionModalPractical {
  id: string;
  content: string;
  otherContent: string;
}

export interface ContactInterface {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  certificationId: string;
  certificationLeaderboardConsent: boolean;
  isInternalUser?: boolean;
}

export interface AwardContactInterface {
  email: string;
  firstName: string;
  lastName: string;
}

export interface LandingPageStatsInterface {
  totalCertifications: number;
  totalActiveExams: number;
}

export interface VerificationState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  certifications: CertificationsPayload[];
  awards: AwardsPayload[];
}

export interface GetExamsRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: RegisteredExamsPayload[];
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface GetExamPaperRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: any | null;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}
export interface SubmitAnswerSheetState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: any | null;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface AddUserFeedbackRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: any | null;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface GetCertificationsRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: GetCertificationsRequestResponse;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface GetCertificationsRequestResponse {
  awardededCertifications: CertificationsPayload[] | [];
  expiredCertifications: CertificationsPayload[] | [];
}

export interface GetAwardsRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: AwardsPayload[];
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface GetContactRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: ContactInterface;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface GetAppStatusRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: AppStatus;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface GetLandingPageStats {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: LandingPageStatsInterface;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface GetLeaderboardRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: LeaderboardResponse;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export enum AwardTemplateType {
  YEARLY = "Template Yearly",
  QUARTERLY = "Template Quarterly",
}

export type CandidateContact = ContactInterface | AwardContactInterface | null;

export type DocumentState = AwardsPayload | CertificationsPayload;

export type DocumentType = DocumentTypes | AwardTemplateType | null;

export enum DocumentTypes {
  CERTIFICATE = "certificate",
  AWARD = "award",
}

export interface GetUserLeaderboardRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: UserLeaderboardData;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface UserLeaderboardData {
  userPosition?: CertificateHolder|null;
  currentQuarterLeaderboard?: LeaderboardQuarter|null;
}

export interface UpdateUserLeaderboardConsentRequestState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: any | null;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

export interface UpdateUserLeaderboardConsentRequestPayload {
  consent: boolean;
}

export interface UpdateUserLeaderboardConsentRequestProps {
  certificateID: string;
  consent: UpdateUserLeaderboardConsentRequestPayload
}

export interface MinimalDocumentData {
  id: string;
  displayName: string;
  contact: {
    certificationId: string;
  };
}
